// useLogout.ts — Sprint 14. The single, authoritative logout path.
// Used by both the dropdown's Sign Out item AND the confirm-modal
// button so a future regression in either path can't leave the user
// "half logged out".
//
// Hotfix C1 (E2E finding): the dropdown's Sign Out used to call
// `setLogoutModalVisible(true)` only — but the modal never actually
// opened (likely an Antd v6 Menu/Dropdown closure interaction with
// `setUserDropdownOpen(false)` happening in the same handler). The
// user could click Sign Out forever and stay authenticated. Going
// direct to the side-effects bypasses the broken modal trigger.
//
// Token cleanup is layered:
//   - useAuth().logout() (UnifiedAuthContext) clears its per-app keys
//     and chains into condoAuthStore.logout()
//   - We additionally call clearTokens() to remove the Sprint 2
//     `refreshToken` key that UnifiedAuthContext doesn't know about.
//   - Persisted Zustand state (`auth-storage`) is cleared by the
//     authStore.logout() inside the chain. Removed AFTER set() runs
//     (Sprint 10 fix) so persist middleware can't re-write it.
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearTokens } from '../../../services/httpClient';

export interface UseLogoutArgs {
  /** The UnifiedAuthContext logout callback. */
  unifiedLogout: () => void;
  onBeforeLogout?: () => void;
}

/** Returns a stable `performLogout` callback. The orchestrator wires
 *  it to both the dropdown's Sign Out item AND the modal's confirm
 *  button — keeping a single function as the source of truth. */
export function useLogout({ unifiedLogout, onBeforeLogout }: UseLogoutArgs) {
  const navigate = useNavigate();

  return useCallback(() => {
    onBeforeLogout?.();
    try {
      unifiedLogout();
    } catch (e) {
      // Never let a logout failure leave the user trapped on the dashboard.
      // eslint-disable-next-line no-console
      console.warn('UnifiedAuth logout threw — proceeding with direct cleanup', e);
    }
    // Defense-in-depth: forcibly clear every key any auth path might own.
    try {
      clearTokens();
      localStorage.removeItem('auth-storage');
      localStorage.removeItem('unified_auth_current_app');
    } catch { /* swallow */ }
    navigate('/login-condo-app/');
  }, [unifiedLogout, onBeforeLogout, navigate]);
}
